package com.islamicmedia.shared.domain.model

/**
 * Which accounts (device + owner — see [ReferralInfoCache.owner], "email|ENV") have already been
 * shown the one-time "Did a friend send you a code?" popup (MediaCheckerUiState.
 * referralRedeemPromptEligible, ReferralCopy.REDEEM_PROMPT_*).
 *
 * A device can hold more than one account over its lifetime (sign out, sign in as someone else),
 * so this keeps a SET of owners rather than one flag: an owner already in the set must never be
 * shown the popup again, and a different owner (or the same email on the other backend) must
 * still see it once. Every SettingsStorage actual persists the encoded form under one key
 * (SettingsStorage.hasSeenReferralRedeemPrompt / markReferralRedeemPromptSeen) and — deliberately,
 * like ReferralInfoCache's siblings — is NOT cleared by clearUserData: it is already scoped per
 * account, so signing out must not un-see it for the account that just left.
 */
internal object ReferralRedeemPromptSeen {
    /** True when [owner] is already in the set [raw] encodes. */
    fun hasSeen(raw: String?, owner: String): Boolean = decode(raw).contains(owner)

    /** [raw] with [owner] added, ready to persist. Idempotent: marking an owner already present
     *  returns an equivalent set (encoded fresh, so callers never need to special-case "already
     *  there"). */
    fun markSeen(raw: String?, owner: String): String = encode(decode(raw) + owner)

    private fun decode(raw: String?): Set<String> =
        raw?.split(',')?.map { it.trim() }?.filter { it.isNotEmpty() }?.toSet() ?: emptySet()

    private fun encode(owners: Set<String>): String = owners.joinToString(",")
}
